import { api, ApiError } from "../../api/client";
import type { AppData } from "../../types";
import { button, el, statusLabel } from "../atoms/dom";
import { dateText, empty, panel, timeText } from "./dashboard_page";

export function appointmentDetailPage(
  data: AppData,
  appointmentId: string,
  actions: {
    onBack: () => void;
    onOpenCustomer: (customerId: string) => void;
    onChanged: () => Promise<void>;
  },
) {
  const appointment = data.appointments.find((item) => item.id === appointmentId);
  if (!appointment) {
    return detailShell(
      actions.onBack,
      "Randevu bulunamadı",
      empty("Randevu silinmiş veya bu işletmenin erişim alanında olmayabilir."),
    );
  }
  const customer = data.customers.find((item) => item.id === appointment.customerId);
  const service = data.services.find((item) => item.id === appointment.serviceId);
  const staff = data.staff.find((item) => item.id === appointment.staffId);
  const branch = data.branches.find((item) => item.id === appointment.branchId);
  const organizationId = data._meta.selectedContext.organizationId;
  const canWrite = (data.permissionsByOrganization[organizationId] ?? []).includes("appointments.write");
  const feedback = el("p", { className: "form-feedback", attrs: { role: "status" } });
  const editPanel = buildEditPanel();
  editPanel.hidden = true;

  const actionBar = el("div", { className: "detail-actions" });
  if (canWrite && appointment.status !== "cancelled") {
    actionBar.append(
      button("Randevuyu düzenle", () => {
        editPanel.hidden = !editPanel.hidden;
        if (!editPanel.hidden) editPanel.scrollIntoView({ behavior: "smooth", block: "start" });
      }),
      button("Randevuyu iptal et", showCancelSheet, "danger"),
    );
  }

  const content = el(
    "div",
    { className: "page-stack" },
    el(
      "section",
      { className: "detail-hero" },
      el(
        "div",
        {},
        el("span", { className: "eyebrow", text: "RANDEVU DETAYI" }),
        el("h2", { text: customer?.displayName ?? "Anonim müşteri" }),
        customer
          ? el("a", { text: customer.phoneNumber, attrs: { href: `tel:${customer.phoneNumber}` } })
          : null,
      ),
      statusLabel(appointment.status),
    ),
    el(
      "div",
      { className: "detail-grid" },
      panel(
        "Randevu bilgileri",
        el(
          "div",
          { className: "settings-list" },
          detailRow("Hizmet", service?.name ?? "Bilinmiyor"),
          detailRow("Tarih", dateText(new Date(appointment.startsAt))),
          detailRow("Saat", `${timeText(appointment.startsAt)} – ${timeText(appointment.endsAt)}`),
          detailRow("Ekip üyesi", staff?.name ?? "Atanmamış"),
          detailRow("Şube", branch?.name ?? "Bilinmiyor"),
          detailRow("Kaynak", sourceLabel(appointment.source)),
        ),
      ),
      panel(
        "Müşteri",
        customer
          ? el(
              "div",
              { className: "detail-customer" },
              el("span", { className: "avatar avatar--large", text: initials(customer.displayName) }),
              el("h3", { text: customer.displayName }),
              el("a", { text: customer.phoneNumber, attrs: { href: `tel:${customer.phoneNumber}` } }),
              button("Müşteri profilini aç", () => actions.onOpenCustomer(customer.id), "quiet"),
            )
          : empty("Müşteri kaydı bulunamadı."),
      ),
    ),
    actionBar,
    editPanel,
    feedback,
  );

  return detailShell(actions.onBack, "Randevu", content);

  function buildEditPanel() {
    const availableServices = data.services.filter(
      (item) => item.isBookable && (item.branchIds.length === 0 || item.branchIds.includes(appointment!.branchId)),
    );
    const serviceSelect = el("select", { className: "form-control", attrs: { "aria-label": "Hizmet" } });
    for (const item of availableServices) {
      const option = el("option", { text: `${item.name} · ${item.durationMinutes} dakika`, attrs: { value: item.id } });
      option.selected = item.id === appointment!.serviceId;
      serviceSelect.append(option);
    }
    const staffSelect = el("select", { className: "form-control", attrs: { "aria-label": "Ekip üyesi" } });
    staffSelect.append(el("option", { text: "Atanmamış", attrs: { value: "" } }));
    for (const person of data.staff.filter((item) => item.active && item.branchId === appointment!.branchId)) {
      const option = el("option", { text: `${person.name}${person.profession ? ` · ${person.profession}` : ""}`, attrs: { value: person.id } });
      option.selected = person.id === appointment!.staffId;
      staffSelect.append(option);
    }
    const startsAtInput = el("input", {
      className: "form-control",
      attrs: { type: "datetime-local", value: localDateTime(appointment!.startsAt), "aria-label": "Randevu tarihi ve saati" },
    });
    const save = button("Değişiklikleri kaydet", () => void saveChanges());
    return el(
      "section",
      { className: "panel edit-panel" },
      el("header", { className: "panel__header" }, el("h2", { text: "Randevuyu düzenle" })),
      el(
        "div",
        { className: "edit-form" },
        formField("Hizmet", serviceSelect),
        formField("Ekip üyesi", staffSelect),
        formField("Tarih ve saat", startsAtInput),
        el("p", { className: "form-hint", text: "Hizmet süresi ve müsaitlik sunucuda tekrar kontrol edilir." }),
        save,
      ),
    );

    async function saveChanges() {
      if (!serviceSelect.value || !startsAtInput.value) {
        setFeedback("Hizmet, tarih ve saat alanlarını doldurun.", true);
        return;
      }
      save.disabled = true;
      save.textContent = "Kaydediliyor…";
      try {
        await api.updateAppointment(appointment!.id, {
          serviceId: serviceSelect.value,
          staffId: staffSelect.value || null,
          startsAt: new Date(startsAtInput.value).toISOString(),
        });
        setFeedback("Randevu başarıyla güncellendi.");
        await actions.onChanged();
      } catch (error) {
        setFeedback(errorMessage(error), true);
      } finally {
        save.disabled = false;
        save.textContent = "Değişiklikleri kaydet";
      }
    }
  }

  function showCancelSheet() {
    const backdrop = el(
      "div",
      { className: "modal-backdrop", attrs: { role: "presentation" } },
      el(
        "section",
        { className: "modal-sheet", attrs: { role: "dialog", "aria-modal": "true", "aria-labelledby": "cancel-title" } },
        el("div", { className: "modal-sheet__handle" }),
        el("h2", { text: "Randevu iptal edilsin mi?", attrs: { id: "cancel-title" } }),
        el("p", { text: `${customer?.displayName ?? "Müşteri"} için oluşturulan randevu iptal edilecek.` }),
        el(
          "div",
          { className: "modal-sheet__actions" },
          button("Vazgeç", () => backdrop.remove(), "quiet"),
          button("Randevuyu iptal et", () => void cancel(backdrop), "danger"),
        ),
      ),
    );
    content.append(backdrop);
  }

  async function cancel(backdrop: HTMLElement) {
    const cancelButton = backdrop.querySelector<HTMLButtonElement>(".button--danger");
    if (cancelButton) {
      cancelButton.disabled = true;
      cancelButton.textContent = "İptal ediliyor…";
    }
    try {
      await api.cancelAppointment(appointment!.id);
      backdrop.remove();
      setFeedback("Randevu iptal edildi.");
      await actions.onChanged();
    } catch (error) {
      backdrop.remove();
      setFeedback(errorMessage(error), true);
    }
  }

  function setFeedback(message: string, isError = false) {
    feedback.textContent = message;
    feedback.classList.toggle("is-error", isError);
  }
}

function detailShell(onBack: () => void, title: string, content: HTMLElement) {
  return el(
    "div",
    { className: "page-stack" },
    el(
      "div",
      { className: "detail-navigation" },
      button("← Geri", onBack, "quiet"),
      el("strong", { text: title }),
    ),
    content,
  );
}

function detailRow(label: string, value: string) {
  return el("div", { className: "setting-row" }, el("span", { text: label }), el("strong", { text: value }));
}

function formField(label: string, control: HTMLElement) {
  return el("label", { className: "form-field" }, el("span", { text: label }), control);
}

function localDateTime(value: string) {
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function sourceLabel(source: string) {
  return { phone: "Telefon", manual: "Manuel", web: "Web" }[source] ?? source;
}

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toLocaleUpperCase("tr");
}

function errorMessage(error: unknown) {
  if (error instanceof ApiError && error.code === "appointment_conflict") {
    return "Seçilen tarih ve saatte yeterli boşluk bulunmuyor.";
  }
  if (error instanceof ApiError && error.status === 403) {
    return "Bu randevuyu değiştirmek için yetkiniz bulunmuyor.";
  }
  return "İşlem tamamlanamadı. Lütfen tekrar deneyin.";
}
